/**
 * A/B Test Page Switcher (internal tool — /pages/a-b-test-switcher)
 *
 * Forces the browser into a chosen FigPii variation by setting FigPii's own per-experiment
 * targeting cookie, then reloads so the engine re-decides.
 *
 * GENERIC by design. The cookie name is FigPii's own convention — their engine reads and writes
 * `EXPERIMENT_COOKIE_BASE_NAME + "-" + toolId` (base "FIGPII_TE") at both sites — so every button
 * on the page carries its own data-tool and this file needs no per-experiment knowledge. It used
 * to hardcode `FIGPII_TE-360160` and a single domain.
 *
 * Config comes from the section container's data attributes, which the Super App fills from the
 * `ab_switcher.experiments` metafield.
 *
 * 🚨 EXCLUSION GROUPS. A test in a FigPii exclusion group is gated by a SECOND cookie,
 * `FIGPII_TE-<group>=<toolId>` (group "A" since 2026-10). The engine reads that group cookie first
 * and keeps the visitor in whatever test it names, so writing only `FIGPII_TE-<toolId>` for another
 * member of the group silently does nothing. Forcing therefore also writes the group cookie, and
 * Clear removes it only when it points at the test being cleared. See forceVariation() and the
 * footgun "FigPii Exclusion Groups Pin a Visitor With a Second Cookie".
 */
(function () {
  'use strict';

  // -------------------------------------------------------------------------
  // NAME DECODING
  //
  // FigPii test names are operator-authored and consistently shaped, but they
  // read as noise: "R_PDP_Remove_Sticky_CTA_090126". Measured across all 50
  // experiments in the Super App's portfolio on 2026-09-18, the convention is:
  //
  //   [owner letter] _ [surface(s)] _ [subject words] _ [MMDDYY | MMDDYYYY]
  //   plus optional status suffixes: IPTargeted, "- flushed", "-copy"
  //
  //   surface tokens seen: PLP 18, HP 15, PDP 9, Cart 7, SW 4
  //   leading owner letter: 43/50 (R, plus M and D)
  //   embedded date:        24/50
  //
  // This decodes that DETERMINISTICALLY. It does NOT invent a description:
  // nothing in the payload says what a test actually changes (the Super App
  // stores no hypothesis for FigPii, exposures.pageUrl is 100% NULL across
  // 1.4M rows, and goalsJson records THAT a goal matches on url but stores no
  // pattern). A generated summary would be confabulation on the one page whose
  // job is forcing yourself into a specific live experiment, so the raw name is
  // always kept on screen beside the decoded one.
  //
  // 🚨 THE DATE IS THE ONE IN THE NAME, NOT THE LAUNCH DATE, and the label must
  // keep saying so. Verified against real launchedAt values: 10/10 matched, but
  // R_PLP_Multiple_Image_Display_061726 actually launched 2026-06-24, and
  // ..._033126 appears twice with different launch dates. The name carries
  // intent; only the Super App knows when a test really started.
  // -------------------------------------------------------------------------

  var SURFACE_LABELS = {
    HP: 'Homepage',
    PDP: 'Product page',
    PLP: 'Collection page',
    SW: 'Sitewide',
    CART: 'Cart',
    CHECKOUT: 'Checkout',
  };

  // Tokens that must never be sentence-cased down to "Cta" / "Aa".
  var ACRONYMS = {
    CTA: 1,
    AA: 1,
    AB: 1,
    PDP: 1,
    PLP: 1,
    HP: 1,
    SW: 1,
    FAQ: 1,
    UI: 1,
    UX: 1,
    ATC: 1,
    SKU: 1,
    USP: 1,
    CRO: 1,
    PPC: 1,
    IP: 1,
  };

  // Joins two surface tokens: "R_HP_AND_PDP_..." is one test on two surfaces.
  var CONNECTORS = { AND: 1, '&': 1, PLUS: 1 };

  var MONTHS = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  /** MMDDYY / MMDDYYYY -> "Sep 1, 2026". Returns null unless it is a real date. */
  function formatNameDate(mm, dd, yy) {
    var m = parseInt(mm, 10);
    var d = parseInt(dd, 10);
    var y = parseInt(yy, 10);
    if (!(m >= 1 && m <= 12) || !(d >= 1 && d <= 31)) return null;
    if (yy.length === 2) y += 2000;
    if (y < 2000 || y > 2100) return null;
    return MONTHS[m - 1] + ' ' + d + ', ' + y;
  }

  /**
   * Decode one FigPii name.
   * @returns {{surfaces:string[], subject:string, date:string|null, statuses:string[], raw:string}}
   *          `subject` falls back to the raw name rather than ever being empty.
   */
  function decodeExperimentName(raw) {
    var original = typeof raw === 'string' ? raw : '';
    var work = original;
    var statuses = [];

    // 1. Status markers. Stripped from the subject, surfaced separately — these
    //    explain traffic shape, which is exactly what the stale grouping is about.
    // 🚨 NO \b ANCHORS HERE. Underscore is a \w character, so \b does NOT match
    //    between "_" and "I" — a \bIP...\b pattern silently failed on every
    //    "..._IPTargeted" name in the corpus, which is most of them. The tokens
    //    are distinctive enough unanchored; removal eats the leading separator.
    //    But they DO need separator boundaries on both sides, or the pattern is
    //    loose in the mirror-image way: unanchored /flushed/ matches inside
    //    "Unflushed" and unanchored /IPTargeted/ inside "Skiptargeted", both of
    //    which strip real subject text and raise a false status. Separators
    //    here are start-of-string, "_", "-" and space — the same set the
    //    tokenizer splits on.
    var IP_TARGETED = /(?:^|[_\s-])IP[\s_-]?Targeted(?=$|[_\s-])/i;
    var FLUSHED = /(?:^|[_\s-])flushed(?=$|[_\s-])/i;
    var COPY = /(?:-copy)+(?=$|[_\s-])/i;
    if (IP_TARGETED.test(work)) statuses.push('IP-targeted');
    if (FLUSHED.test(work)) statuses.push('flushed');
    if (COPY.test(work)) statuses.push('copy');
    if (/^test[_\s-]/i.test(work)) statuses.push('test harness');
    work = work
      .replace(new RegExp(IP_TARGETED.source, 'gi'), ' ')
      .replace(new RegExp(FLUSHED.source, 'gi'), ' ')
      .replace(new RegExp(COPY.source, 'gi'), ' ')
      .replace(/^test[_\s-]+/i, ' ');

    // 2. Date, in any of the three observed spellings. Removed so it cannot be
    //    mistaken for subject words.
    var date = null;
    var m;
    // Separators seen live: "." (03.11.26), "/" (09/18/26, 09/08/2026) and
    // "-". "/" matters doubly because it is ALSO a tokenizer separator, so an
    // unparsed slash date does not merely go unlabelled — it shatters into three
    // stray digit tokens inside the subject ("Consultation headline change 09 18
    // 26", observed on production data 2026-09-18). The month/day/year validation
    // below is what keeps this permissive class from eating non-dates.
    if ((m = work.match(/(\d{2})[._/-](\d{2})[._/-](\d{2,4})/))) {
      date = formatNameDate(m[1], m[2], m[3]);
      if (date) work = work.replace(m[0], ' ');
    }
    if (!date && (m = work.match(/(?:^|[^\d])(\d{2})(\d{2})(\d{4})(?!\d)/))) {
      date = formatNameDate(m[1], m[2], m[3]);
      if (date) work = work.replace(m[1] + m[2] + m[3], ' ');
    }
    if (!date && (m = work.match(/(?:^|[^\d])(\d{2})(\d{2})(\d{2})(?!\d)/))) {
      date = formatNameDate(m[1], m[2], m[3]);
      if (date) work = work.replace(m[1] + m[2] + m[3], ' ');
    }

    // 3. Tokenize on every separator the corpus uses: _ - space /
    var tokens = work.split(/[_\-\s/]+/).filter(function (t) {
      return t.length > 0;
    });

    // 4. Leading owner initial (R/M/D). Meaning is not documented anywhere, so
    //    it is dropped from the label rather than guessed at — it survives in
    //    the raw name, which stays on screen.
    if (tokens.length && /^[A-Za-z]$/.test(tokens[0])) tokens.shift();

    // 5. Surfaces, but ONLY while they lead. "R_HP_PLP_Starting_At_Price" has
    //    two; "R_Bestsellers_PLP_Rearrange" has none in leading position, and
    //    its PLP correctly stays inline in the subject where it reads naturally.
    var surfaces = [];
    while (tokens.length) {
      var label = SURFACE_LABELS[tokens[0].toUpperCase()];
      if (!label) {
        // A connector only continues the run when a surface actually follows it,
        // so "HP_AND_PDP" yields two chips while a subject that merely begins
        // with "and" keeps its word. Without the lookahead, the connector would
        // be swallowed from names where it is ordinary prose.
        if (
          CONNECTORS[tokens[0].toUpperCase()] &&
          tokens.length > 1 &&
          SURFACE_LABELS[tokens[1].toUpperCase()]
        ) {
          tokens.shift();
          continue;
        }
        break;
      }
      if (surfaces.indexOf(label) === -1) surfaces.push(label);
      tokens.shift();
    }

    // 6. Sentence-case the remainder, preserving acronyms and lowercasing "vs".
    var words = tokens.map(function (t, i) {
      var up = t.toUpperCase();
      if (ACRONYMS[up]) return up;
      if (up === 'VS') return 'vs';
      // A short token with no lowercase letter was cased deliberately by the
      // author ("V1", "(I)", "AA") — preserve rather than sentence-case it.
      if (t.length <= 4 && !/[a-z]/.test(t)) return t;
      var lower = t.toLowerCase();
      return i === 0 ? lower.charAt(0).toUpperCase() + lower.slice(1) : lower;
    });

    var subject = words.join(' ').replace(/\s+/g, ' ').trim();
    if (!subject) subject = original;

    return {
      surfaces: surfaces,
      subject: subject,
      date: date,
      statuses: statuses,
      raw: original,
    };
  }

  // Exposed BEFORE the DOM guard below so the decoder is unit-testable without
  // a rendered page.
  window.MurphyDoorABSwitcher = window.MurphyDoorABSwitcher || {};
  window.MurphyDoorABSwitcher.decodeExperimentName = decodeExperimentName;

  var root = document.querySelector('.ab-test-switcher');
  if (!root) return;

  var COOKIE_BASE = root.getAttribute('data-cookie-base') || 'FIGPII_TE';
  var COOKIE_DOMAIN = root.getAttribute('data-cookie-domain') || '';
  var MAX_AGE = parseInt(root.getAttribute('data-cookie-max-age'), 10);
  if (!isFinite(MAX_AGE) || MAX_AGE <= 0) MAX_AGE = 86400;

  function cookieName(toolId) {
    return COOKIE_BASE + '-' + toolId;
  }

  /**
   * Write the cookie. The domain attribute is OMITTED when the app could not resolve a usable
   * one (a single-label host cannot carry a domain-scoped cookie) — a host-only cookie still
   * works on the current host, which is strictly better than emitting an invalid attribute that
   * the browser silently drops.
   */
  function writeCookie(toolId, value, maxAgeSeconds) {
    var parts = [
      cookieName(toolId) + '=' + encodeURIComponent(value),
      'path=/',
      'max-age=' + maxAgeSeconds,
      'SameSite=Lax',
    ];
    if (COOKIE_DOMAIN) parts.push('domain=' + COOKIE_DOMAIN);
    document.cookie = parts.join(';');
  }

  /**
   * Expire a cookie. When a domain is configured, the host-only twin is expired too: a cookie
   * written before the domain was known (or by an older version of this page) would otherwise
   * survive and keep steering FigPii.
   */
  function deleteCookie(suffix) {
    writeCookie(suffix, '', 0);
    if (COOKIE_DOMAIN) {
      document.cookie = cookieName(suffix) + '=;path=/;max-age=0;SameSite=Lax';
    }
  }

  /** Every cookie in the jar as {name: value}. First occurrence wins, matching readCookie. */
  function cookieJar() {
    var out = {};
    var jar = document.cookie ? document.cookie.split(';') : [];
    for (var i = 0; i < jar.length; i++) {
      var c = jar[i].trim();
      var eq = c.indexOf('=');
      if (eq <= 0) continue;
      var name = c.slice(0, eq);
      if (name in out) continue;
      var raw = c.slice(eq + 1);
      try {
        out[name] = decodeURIComponent(raw);
      } catch (e) {
        out[name] = raw;
      }
    }
    return out;
  }

  function readCookie(toolId) {
    var jar = cookieJar();
    var name = cookieName(toolId);
    return name in jar ? jar[name] : null;
  }

  // FigPii exclusion-group names are single uppercase letters ("A"); tool ids are numeric, so the
  // two cookie families sharing the FIGPII_TE- prefix never collide.
  var GROUP_NAME = /^[A-Z]$/;

  /** Names of every FigPii exclusion-group cookie currently set, e.g. ["A"]. */
  function groupCookieNames() {
    var jar = cookieJar();
    var prefix = COOKIE_BASE + '-';
    var groups = [];
    for (var name in jar) {
      if (name.indexOf(prefix) !== 0) continue;
      var suffix = name.slice(prefix.length);
      if (GROUP_NAME.test(suffix)) groups.push(suffix);
    }
    return groups;
  }

  /**
   * The exclusion group FigPii itself has for a test, read from its runtime settings.
   *
   * The engine assigns `window.FIGPII.settings` from the decompressed domain settings
   * (`tracking-settings.figpii.com/<hash>.json`, key `Eg` -> `exclusionGroup`), keyed by tool id.
   * The Super App payload carries no group, so this is the only source of truth on the page.
   *
   * @returns {string|null} the group name, or null when FigPii is not on the page (staff mode,
   *   blocked, not loaded yet) or the test is in no group. `undefined` vs `null` is not
   *   distinguished: either way the caller falls back.
   */
  function exclusionGroupFor(toolId) {
    try {
      var settings = window.FIGPII && window.FIGPII.settings;
      var exps = settings && settings.experiments;
      if (!exps || typeof exps !== 'object') return null;
      var exp = exps[toolId];
      if (!exp) {
        for (var k in exps) {
          if (exps[k] && String(exps[k].id) === String(toolId)) {
            exp = exps[k];
            break;
          }
        }
      }
      var group = exp && exp.exclusionGroup;
      return typeof group === 'string' && group ? group : null;
    } catch (e) {
      return null;
    }
  }

  /** True once FigPii's runtime settings are readable, i.e. "no group" is an answer, not a gap. */
  function figpiiSettingsAvailable() {
    try {
      return !!(
        window.FIGPII &&
        window.FIGPII.settings &&
        window.FIGPII.settings.experiments
      );
    } catch (e) {
      return false;
    }
  }

  /**
   * Force a variation, including the exclusion-group cookie FigPii checks first.
   *
   * How the engine decides a group (read from tracking-cdn.figpii.com, 2026-10-02):
   *   1. FIGPII_TE-<group> names a member  -> that test, no further questions
   *   2. the group has one test            -> that test
   *   3. exactly one member has its own FIGPII_TE-<toolId> cookie -> that test
   *   4. otherwise                         -> random member, written to FIGPII_TE-<group>
   * So writing only the tool cookie loses to step 1 whenever a stale group cookie exists, and
   * merely DELETING the group cookie still loses at step 3/4 when the visitor also holds the old
   * test's tool cookie (which they do, having been bucketed into it). Writing the group cookie is
   * the only deterministic route.
   *
   * Group known (FigPii settings on the page): write FIGPII_TE-<group>=<toolId>.
   * Group unknown (FigPii absent): point every existing group cookie at this test. For a member
   * that is exactly right; for a non-member FigPii sees an id outside the group, ignores it and
   * re-decides from steps 2-4, the same outcome as deleting the cookie.
   */
  function forceVariation(toolId, variationId) {
    writeCookie(toolId, variationId, MAX_AGE);
    var group = exclusionGroupFor(toolId);
    if (group) {
      writeCookie(group, toolId, MAX_AGE);
      return;
    }
    if (figpiiSettingsAvailable()) return; // FigPii says: no group. Leave groups alone.
    var groups = groupCookieNames();
    for (var i = 0; i < groups.length; i++)
      writeCookie(groups[i], toolId, MAX_AGE);
  }

  /**
   * Clear a forced test. Group cookies are removed only when they point at THIS test: a group
   * cookie naming a different test is FigPii's own assignment and is not ours to undo.
   */
  function clearVariation(toolId) {
    deleteCookie(toolId);
    var jar = cookieJar();
    var groups = groupCookieNames();
    for (var i = 0; i < groups.length; i++) {
      if (jar[cookieName(groups[i])] === String(toolId))
        deleteCookie(groups[i]);
    }
  }

  /**
   * Staff mode (snippets/md-internal-traffic.liquid) strips FigPii from the page, so nothing on
   * this page can take effect. The flag is decided synchronously in <head>, before this file runs.
   */
  var STAFF_REASONS = { staff_cookie: 1, staff_link: 1 };
  function isStaffMode() {
    return (
      window.MD_INTERNAL_TRAFFIC === true &&
      STAFF_REASONS[window.MD_INTERNAL_TRAFFIC_REASON] === 1
    );
  }

  /**
   * The notice ships as static, hidden markup and is revealed during page load, before anyone
   * can interact. That is initial content, not a status change, so it is deliberately NOT a live
   * region: an announcement there would only repeat what the reading order already reaches first.
   */
  function showStaffNotice() {
    var notice = root.querySelector('[data-staff-notice]');
    if (notice) notice.hidden = !isStaffMode();
  }

  /** Mark whichever button matches the cookie currently in force, so the page shows your state. */
  function markActive() {
    var seen = {};
    var buttons = root.querySelectorAll(
      '.ab-test-switcher__btn[data-tool][data-variant]'
    );
    for (var i = 0; i < buttons.length; i++) {
      var btn = buttons[i];
      var tool = btn.getAttribute('data-tool');
      if (!(tool in seen)) seen[tool] = readCookie(tool);
      var isActive =
        seen[tool] !== null && seen[tool] === btn.getAttribute('data-variant');
      btn.classList.toggle('ab-test-switcher__btn--active', isActive);
      btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    }
    // A Clear control is only meaningful while an override is actually in force. It ships HIDDEN
    // from Liquid and is revealed here, so the page never flashes a control that does nothing.
    var wraps = root.querySelectorAll(
      '.ab-test-switcher__clear-wrap[data-clear-wrap]'
    );
    for (var j = 0; j < wraps.length; j++) {
      var forced =
        readCookie(wraps[j].getAttribute('data-clear-wrap')) !== null;
      wraps[j].hidden = !forced;
    }
  }

  /**
   * Replace each ugly raw heading with its decoded form, keeping the raw name
   * visible underneath.
   *
   * Built with DOM APIs only, never innerHTML: every name here is
   * CLIENT-SOURCED (captured from FigPii's own settings by our beacon), so it
   * is untrusted display data. Liquid escaped it on render and textContent
   * keeps it inert.
   *
   * Progressive enhancement — if this never runs, the page still shows the raw
   * names exactly as before.
   */
  function decorateExperimentNames() {
    var blocks = root.querySelectorAll('.ab-test-switcher__experiment');
    for (var i = 0; i < blocks.length; i++) {
      var block = blocks[i];
      var heading = block.querySelector('.ab-test-switcher__exp-name');
      if (!heading || heading.getAttribute('data-decoded') === 'true') continue;

      var raw = (heading.textContent || '').trim();
      if (!raw) continue;
      var info = decodeExperimentName(raw);

      heading.textContent = '';
      for (var s = 0; s < info.surfaces.length; s++) {
        var chip = document.createElement('span');
        chip.className = 'ab-test-switcher__exp-surface';
        chip.textContent = info.surfaces[s];
        heading.appendChild(chip);
      }
      var subject = document.createElement('span');
      subject.className = 'ab-test-switcher__exp-subject';
      subject.textContent = info.subject;
      heading.appendChild(subject);
      heading.setAttribute('data-decoded', 'true');

      // The raw name STAYS. It is what you match against in the FigPii console,
      // and it is the ground truth whenever the decode is imperfect.
      var rawLine = document.createElement('div');
      rawLine.className = 'ab-test-switcher__exp-raw';
      rawLine.textContent = raw;
      heading.parentNode.insertBefore(rawLine, heading.nextSibling);

      // Date + status APPENDED to the meta line, never replacing it: markActive()
      // owns the Clear control that already lives in there.
      var meta = block.querySelector('.ab-test-switcher__exp-meta');
      if (!meta) continue;
      var bits = [];
      // "name dated", not "launched" — the two genuinely disagree in the data.
      if (info.date) bits.push('name dated ' + info.date);
      for (var k = 0; k < info.statuses.length; k++)
        bits.push(info.statuses[k]);
      if (!bits.length) continue;
      var extra = document.createElement('span');
      extra.className = 'ab-test-switcher__exp-decoded-meta';
      extra.textContent = ' · ' + bits.join(' · ');
      var clearWrap = meta.querySelector('.ab-test-switcher__clear-wrap');
      if (clearWrap) meta.insertBefore(extra, clearWrap);
      else meta.appendChild(extra);
    }
  }

  function init() {
    root.addEventListener('click', function (event) {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;

      var clear = target.closest('.ab-test-switcher__clear[data-clear]');
      if (clear) {
        // max-age=0 expires it immediately, handing you back to normal random assignment.
        clearVariation(clear.getAttribute('data-clear'));
        window.location.reload();
        return;
      }

      var btn = target.closest(
        '.ab-test-switcher__btn[data-tool][data-variant]'
      );
      if (!btn) return;
      forceVariation(
        btn.getAttribute('data-tool'),
        btn.getAttribute('data-variant')
      );
      // Relative, so this stays on whatever host you are browsing rather than a hardcoded one.
      window.location.href = '/';
    });

    showStaffNotice();
    decorateExperimentNames();
    markActive();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
